import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowRightLeft,
  ArrowDownCircle,
  ArrowUpCircle,
  Loader2,
  Building2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BankTransaction } from '@/hooks/useBankTransactions';
import { useFinancialAccounts } from '@/hooks/useFinancialAccounts';
import { cn } from '@/lib/utils';

interface CreateTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: BankTransaction | null;
  currentAccountId: string;
  onConfirm: (destinationAccountId: string) => void;
  isLoading?: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Math.abs(value));
};

export const CreateTransferDialog = ({
  open,
  onOpenChange,
  transaction,
  currentAccountId,
  onConfirm,
  isLoading = false,
}: CreateTransferDialogProps) => {
  const [destinationAccountId, setDestinationAccountId] = useState('');

  const { accounts } = useFinancialAccounts();

  // Encontrar a conta atual
  const currentAccount = accounts.find(a => a.id === currentAccountId);
  
  // Filtrar outras contas (excluir a atual)
  const otherAccounts = accounts.filter(a => a.id !== currentAccountId);

  // Reset quando abre
  useEffect(() => {
    if (open) {
      setDestinationAccountId('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (!destinationAccountId) return;
    onConfirm(destinationAccountId);
  };

  if (!transaction) return null;

  const isCredit = transaction.transaction_type === 'credit';
  const destinationAccount = accounts.find(a => a.id === destinationAccountId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Transferência Entre Contas
          </DialogTitle>
          <DialogDescription>
            Marque esta transação como uma transferência entre contas bancárias.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Card da transação bancária */}
          <div className={cn(
            'p-4 rounded-lg border',
            isCredit ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'
          )}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn(
                  'p-2 rounded-full',
                  isCredit ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
                )}>
                  {isCredit ? <ArrowDownCircle className="h-5 w-5" /> : <ArrowUpCircle className="h-5 w-5" />}
                </div>
                <div>
                  <p className="font-medium">{transaction.description}</p>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(`${transaction.transaction_date}T12:00:00`), 'dd/MM/yyyy', { locale: ptBR })}
                  </p>
                </div>
              </div>
              <p className={cn('text-lg font-bold', isCredit ? 'text-green-600' : 'text-red-600')}>
                {isCredit ? '+' : '-'}{formatCurrency(transaction.value)}
              </p>
            </div>
          </div>

          {/* Visualização da transferência */}
          <div className="flex items-center justify-center gap-4">
            <div className="flex-1 p-3 bg-muted rounded-lg text-center">
              <Building2 className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-sm font-medium">{currentAccount?.account_name || 'Conta Atual'}</p>
              <p className="text-xs text-muted-foreground">
                {isCredit ? 'Destino' : 'Origem'}
              </p>
            </div>
            <ArrowRightLeft className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <div className={cn(
              'flex-1 p-3 rounded-lg text-center border-2 border-dashed',
              destinationAccountId ? 'bg-primary/5 border-primary/30' : 'bg-muted border-muted-foreground/20'
            )}>
              <Building2 className={cn('h-5 w-5 mx-auto mb-1', destinationAccountId ? 'text-primary' : 'text-muted-foreground')} />
              <p className="text-sm font-medium">
                {destinationAccount?.account_name || 'Selecione a conta'}
              </p>
              <p className="text-xs text-muted-foreground">
                {isCredit ? 'Origem' : 'Destino'}
              </p>
            </div>
          </div>

          {/* Seletor de conta destino */}
          <div className="space-y-2">
            <Label>
              {isCredit ? 'Conta de Origem' : 'Conta de Destino'} *
            </Label>
            <Select value={destinationAccountId} onValueChange={setDestinationAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a conta" />
              </SelectTrigger>
              <SelectContent>
                {otherAccounts.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    Nenhuma outra conta disponível
                  </div>
                ) : (
                  otherAccounts.map(account => (
                    <SelectItem key={account.id} value={account.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <span>{account.account_name}</span>
                        <span className="text-muted-foreground text-xs">
                          ({formatCurrency(account.current_balance)})
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!destinationAccountId || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <ArrowRightLeft className="h-4 w-4 mr-2" />
                Confirmar Transferência
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
